<template>
  <section
    class="bg-white mx-auto mb-16 px-20 pt-12 rounded-lg"
    style="max-width: 800px; padding-bottom: 36px"
  >
    <form @submit="onSubmit">
      <Field
        v-slot="{ field, handleChange, errorMessage, meta }"
        v-for="input in formInputs[0]"
        :key="input.name"
        :name="input.name"
        :validateOnMount="true"
        :validateOnInput="true"
      >
        <BaseTextInput
          required
          :name="input.name"
          :label="input.label"
          :placeholder="input.placeholder"
          :field="field"
          :error-message="errorMessage && meta.dirty ? errorMessage : ''"
          :on-change="handleChange"
          class="mb-9"
        />
      </Field>

      <RegistrationFormParts
        v-for="form in groupFormData"
        :key="form.id"
        :id="'target' + form.id"
        :form="form"
        :errors="errors"
        :hiddenItems="hiddenItems"
      />

      <Field
        v-slot="{ field, handleChange, errorMessage, meta }"
        v-for="input in formInputs[1]"
        :key="input.name"
        :name="input.name"
        :validateOnMount="true"
        :validateOnInput="true"
      >
        <BaseTextInput
          required
          :type="input.type"
          :name="input.name"
          :label="input.label"
          :placeholder="input.placeholder"
          :field="field"
          :error-message="errorMessage && meta.dirty ? errorMessage : ''"
          :on-change="handleChange"
          class="mb-9"
        />
      </Field>

      <button
        type="submit"
        class="block mx-auto mt-3 mb-8 bg-coral-main font-semibold text-white rounded focus:outline-none h-10"
        :class="[
          Object.keys(errors).length > 0
            ? 'bg-slate-300 cursor-not-allowed'
            : 'bg-coral-main hover:button',
        ]"
        style="width: 200px"
      >
        次へ
      </button>
    </form>
    <div class="text-center">
      <button
        class="inline-block text-gray-62 leading-6 border-b focus:outline-none"
        v-show="hasPrevious"
        @click="stepBack"
      >
        ひとつ前の項目に戻る
      </button>
    </div>
  </section>
</template>

<script>
import { reactive, computed, toRefs } from 'vue';
import { Field, useForm, useFormValues } from 'vee-validate';

import axios from '@/utils/axios.js';
import rules from '@/utils/validateRules.js';
import useFormValidation from '@/utils/composables/useFormValidation.js';
import BaseTextInput from '@/components/form/BaseTextInput.vue';
import RegistrationFormParts from '@/views/registration/RegistrationFormParts.vue';

export default {
  props: {
    formData: {
      type: Object,
      required: true,
    },
    initialValues: {
      type: Object,
      required: true,
    },
    hasPrevious: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Field,
    BaseTextInput,
    RegistrationFormParts,
  },
  emits: ['change-view'],
  setup(props, { emit }) {
    const formInputs = [
      [
        { name: 'name', label: '氏名', placeholder: '山田 太郎' },
        { name: 'tel', label: '電話番号', placeholder: '080-0000-0000' },
      ],
      [
        {
          name: 'email',
          label: 'email',
          placeholder: 'メールアドレス',
          type: 'email',
        },
        { name: 'password', label: 'パスワード', type: 'password' },
        {
          name: 'passwordConfirm',
          label: 'パスワード（確認）',
          type: 'password',
        },
      ],
    ];

    const { groupFormData, validationSchema, detectHiddenItems } =
      useFormValidation({
        formData: props.formData,
        inputGroup: 'account',
        initialSchema: {
          name(value) {
            const result = rules.validate(value, 'required');
            return result === true || result;
          },
          tel(value) {
            const result = rules.validate(value, 'required|tel');
            return result === true || result;
          },
          async email(value) {
            const validateInput = rules.validate(value, 'required|email');
            if (validateInput !== true) return validateInput;

            const validateEmail = await axios
              .get('/users/email', { params: { emails: [value] } })
              .then((res) =>
                res.data.data.length
                  ? '既に登録されているメールアドレスです'
                  : true
              );
            return validateEmail;
          },
          password(value) {
            const result = rules.validate(
              value,
              'required|alpha_num_special|min:8|max:100'
            );
            return result === true || result;
          },
          passwordConfirm(value) {
            const result = rules.validate(value, 'required');
            if (result !== true) return result;

            return value === values.value.password || '入力内容が一致しません';
          },
        },
      });

    const { handleSubmit, errors, setFieldError } = useForm({
      initialValues: props.initialValues,
      validationSchema,
      validateOnMount: true,
    });
    const values = useFormValues();

    const state = reactive({
      hiddenItems: computed(() => {
        const hiddenItems = detectHiddenItems(values.value);
        Object.entries(hiddenItems).forEach(([key, value]) => {
          if (value) return;
          setFieldError(`itemId${key}`, '');
        });

        return hiddenItems;
      }),
    });

    const stepBack = () => {
      emit('change-view', { view: 'step1', inputValues: values.value });
    };

    const onSubmit = handleSubmit((inputValues) => {
      emit('change-view', { view: 'step3', inputValues });
    });

    return {
      ...toRefs(state),
      formInputs,
      groupFormData,
      errors,
      stepBack,
      onSubmit,
    };
  },
};
</script>
